import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"

// Generic multi-select filter: a Select-like trigger that opens a searchable,
// checkbox-style list so multiple options can be picked at once. Selecting
// zero options is treated as "all" by the caller.
export const MultiSelect = React.forwardRef(({
  options = [],
  selected = [],
  onChange,
  placeholder = "Select...",
  allLabel = "All",
  className,
  triggerClassName,
  searchable = true,
  "data-testid": testId,
}, ref) => {
  const [open, setOpen] = React.useState(false);

  const toggleValue = (value) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const label = React.useMemo(() => {
    if (selected.length === 0) return allLabel;
    if (selected.length === 1) return options.find((o) => o.value === selected[0])?.label || selected[0];
    return `${selected.length} selected`;
  }, [selected, options, allLabel]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={ref}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("justify-between font-normal", className, triggerClassName)}
          data-testid={testId}
        >
          <span className="truncate">{label}</span>
          <span className="flex items-center gap-1 shrink-0">
            {selected.length > 0 && (
              <X className="h-3.5 w-3.5 opacity-60 hover:opacity-100" onClick={clearAll} data-testid={testId ? `${testId}-clear` : undefined} />
            )}
            <ChevronsUpDown className="h-3.5 w-3.5 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[240px] p-0" align="start">
        <Command>
          {searchable && <CommandInput placeholder={`Search ${placeholder.toLowerCase()}...`} />}
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((opt) => {
                const isSelected = selected.includes(opt.value);
                return (
                  <CommandItem
                    key={opt.value}
                    value={opt.label}
                    onSelect={() => toggleValue(opt.value)}
                    data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary shrink-0",
                        isSelected ? "bg-primary text-primary-foreground" : "opacity-50"
                      )}
                    >
                      <Check className={cn("h-3 w-3", isSelected ? "opacity-100" : "opacity-0")} />
                    </div>
                    <span className="truncate">{opt.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
});
MultiSelect.displayName = "MultiSelect";
